import { useMemo } from "react";
import { getLocale, useIntl } from "umi";
import { IsSingleDay } from "@/pages/dashboard/utils/utils";
import { PERIODTIMEOBJ } from "../../_config/constant";
import { dynamicString } from '@home/utils/dynamicString';

interface Props {
  componentMetrics: any;
  accumated: string;
  gameCode: string;
  filterVal: any;
  data: any;
  titleNameMsg?: any;
  tabMsg?: any;
}

export const useCommonRankShowData = (props: Props) => {
  const { accumated, filterVal, componentMetrics, data, titleNameMsg, tabMsg } = props;
  const { timeType } = filterVal || {};
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const isEn = locale === 'en-US';
  const { title, titleResultObj, prefix, rangeTitle }: { title: string; prefix: string; rangeTitle: string; titleResultObj: any; } = useMemo(() => {
    const titleResultObj: { day: string; week: string; month: string; } = {
      day: componentMetrics?.[isEn ? 'enName' : 'cnName'],
      week: componentMetrics?.[isEn ? 'enWeekName' : 'cnWeekName'],
      month: componentMetrics?.[isEn ? 'enMonthName' : 'cnMonthName'],
    };
    const rangeTitleObj: { day: string; week: string; month: string; } = {
      day: componentMetrics?.[isEn ? 'rangeEnName' : 'rangeCnName'],
      week: componentMetrics?.[isEn ? 'rangeEnWeekName' : 'rangeCnWeekName'],
      month: componentMetrics?.[isEn ? 'rangeEnMonthName' : 'rangeCnMonthName'],
    };
    return {
      title: dynamicString(titleResultObj?.[timeType?.toLocaleLowerCase()], data) || titleResultObj?.['day'],
      prefix: componentMetrics?.prefix,
      rangeTitle: dynamicString(rangeTitleObj?.[timeType?.toLocaleLowerCase()], data),
      titleResultObj,
    };
  }, [locale, timeType, componentMetrics, data]);


  const titleContent = useMemo(() => {
    const titleContentObj: any = {
      country: formatMessage({ id: 'common_country_top_10' }),
      region: formatMessage({ id: 'common_region_top_10' }),
      channel: formatMessage({ id: 'common_channel_top_10' }),
      zone: formatMessage({ id: 'common_zone_top_10' }),
      os: formatMessage({ id: 'common_platfrom_top_10' }),
      lang: formatMessage({ id: 'common_language_top_10' }),
    };

    // 如果 tabMsg 中有对应维度的别名配置，动态添加到 titleContentObj
    // 生成 "of Top 10 XXX" 格式的标题
    if (tabMsg?.[accumated] && !titleContentObj[accumated]) {
      const tabConfig = tabMsg[accumated];
      const aliasName = isEn
        ? (tabConfig.aliasNameEn || tabConfig.nameEn)
        : (tabConfig.aliasNameCn || tabConfig.nameCn);
      if (aliasName) {
        // 生成 "of Top 10 Campaign" 格式
        titleContentObj[accumated] = isEn ? `of Top 10 ${aliasName}` : `Top 10 ${aliasName}`;
      }
    }

    const noTopTitle = accumated === 'os' || accumated === 'lang';
    const isTrim = noTopTitle && !isEn;

    // 优先使用 titleNameMsg 配置
    if (titleNameMsg?.[accumated]) {
      const titleConfig = titleNameMsg[accumated];
      const titleName = isEn
        ? dynamicString(titleConfig.nameEn, data)
        : dynamicString(titleConfig.nameCn, data);
      return {
        name: titleName,
        content: titleName,
      };
    }

    // 标题跟表格名称不一致的情况下 优先使用传入的标题名称
    const propTitle = componentMetrics?.[isEn ? 'enTitleName' : 'cnTitleName'];
    const titleName = propTitle ? propTitle : title;
    const accumatedTitle = titleContentObj?.[accumated];
    // 注意有空格
    return {
      name: isTrim ? `${accumatedTitle}${titleName}` : `${titleName} ${accumatedTitle}`,
      content: isTrim ? `${accumatedTitle}${titleName}` : `${titleName} ${accumatedTitle}`
    };
  }, [locale, title, accumated, titleNameMsg, data, isEn, componentMetrics, tabMsg, formatMessage]);

  const isSingle = useMemo(() => IsSingleDay({
    ...filterVal,
    period: PERIODTIMEOBJ?.[timeType]
  }), [filterVal]);

  // const getParams = useCallback((breakDownTypeVal, metricsNameVal) => {
  //   const [start, end] = rangeTime;
  //   const params = {
  //     game_code: gameCode,
  //     filters: formatFilterValues(filters),
  //     start_time: start,
  //     end_time: end,
  //     by_type: breakDownTypeVal,
  //     metric: metricsNameVal,
  //     period: PERIODTIMEOBJ?.[timeType],
  //     has_top_data: true,
  //     top_num: 10
  //   }
  //   return params
  // }, [accumated, filterVal])

  const comChartConfig = useMemo(() => ({
    isHaveBar: true,
    isHaveLine: true,
    isHavePie: true,
    isHaveShare: true,
    isHaveMap: accumated === 'country'
  }), [accumated]);

  const tableColumns: any = useMemo(() => {
    const titleColumnsObj: any = {
      country: formatMessage({ id: 'common_Country' }),
      region: formatMessage({ id: 'common_Region' }),
      channel: formatMessage({ id: 'common_Channel' }),
      zone: formatMessage({ id: 'common_Zone' }),
      os: formatMessage({ id: 'common_System' }),
      lang: formatMessage({ id: 'common_Language' }),
      bundle: formatMessage({ id: 'common_Bundle' }),
    };

    // 如果 tabMsg 中有对应维度的别名配置，动态添加到 titleColumnsObj
    if (tabMsg?.[accumated]) {
      const tabConfig = tabMsg[accumated];
      const aliasName = isEn
        ? (tabConfig.aliasNameEn || tabConfig.nameEn)
        : (tabConfig.aliasNameCn || tabConfig.nameCn);
      if (aliasName) {
        titleColumnsObj[accumated] = aliasName;
      }
    }

    const result = [
      {
        key: 'name',
        header: dynamicString(titleColumnsObj?.[accumated], data),
        align: 'left',
        dataType: accumated,
      },
      {
        key: componentMetrics?.reqMetricKey,
        header: isSingle ? title : rangeTitle,
        align: 'right',
        dataType: 'integer',
        decimalObj: {
          type: componentMetrics?.type,
          decimal: componentMetrics?.decimal,
          percentDecimals: componentMetrics?.percentDecimals,
        },
        ratioField: componentMetrics?.ratioField || 'change',
        valueName: componentMetrics?.metricKey,
      }
    ];

    if(componentMetrics?.extraComponentMetrics){
      result.push(
        ...componentMetrics?.extraComponentMetrics.map((extra: { [x: string]: any; reqMetricKey: any; dataType: any; type: any; decimal: any; percentDecimals: any; ratioFiled: any; metricKey: any; })=>{
          const titleResultObj: any = {
            day: extra?.[isEn ? 'enName' : 'cnName'],
            week: extra?.[isEn ? 'enWeekName' : 'cnWeekName'],
            month: extra?.[isEn ? 'enMonthName' : 'cnMonthName'],
          };
          const rangeTitleObj: any = {
            day: extra?.[isEn ? 'rangeEnName' : 'rangeCnName'],
            week: extra?.[isEn ? 'rangeEnWeekName' : 'rangeCnWeekName'],
            month: extra?.[isEn ? 'rangeEnMonthName' : 'rangeCnMonthName'],
          };
          const extraTableColumnTitle = dynamicString(titleResultObj?.[timeType?.toLocaleLowerCase()], data) || titleResultObj?.day;
          const extraRangeTableColumnTitle = dynamicString(rangeTitleObj?.[timeType?.toLocaleLowerCase()], data);

          return { 
            key: extra?.reqMetricKey,
            header: isSingle ? extraTableColumnTitle : extraRangeTableColumnTitle,
            align: 'right',
            dataType: extra?.dataType || 'integer',
            decimalObj: {
              type: extra?.type,
              decimal: extra?.decimal,
              percentDecimals: extra?.percentDecimals,
            },
            ratioField: extra?.ratioFiled || '',
            valueName: extra?.metricKey,
          };
        })
      );
    }
    const shareKey = isEn ? componentMetrics?.shareKey || '' : componentMetrics?.shareKeyCn || '';
    result.push(
      {
        key: 'share_of_all',
        header: shareKey || formatMessage({ id: 'overview_Share_Of_All' }),
        align: 'right',
        dataType: 'oneDecimalPercent',
      }
    );

    return result;
  }, [isEn, accumated, componentMetrics, title, rangeTitle, isSingle, tabMsg, formatMessage, timeType]);

  const chartTooltipsName = useMemo(() => dynamicString(titleResultObj?.[timeType?.toLocaleLowerCase()], data), [componentMetrics, titleResultObj, timeType, isEn, data]);

  return {
    title,
    prefix,
    rangeTitle,
    titleContent,
    isSingle,
    comChartConfig,
    componentMetrics,
    tableColumns,
    chartTooltipsName,
  };
};